// Read-only client for /api/kpis/objectives. The Auto Research panel
// populates its objective dropdown from this endpoint. `trust_level` is
// retained as metadata (surfaced in the Claude prompt + study report) but no
// longer gates a start — the experimental-trust opt-in was removed 2026-06-14
// (every rig objective is experimental by design).
//
// Schema mirrors damperlib.api._objective_definition_to_model. We
// don't validate the response with Zod here because /api/kpis is a
// pure read-side surface; a divergent backend would surface as a
// TypeScript runtime error rather than corrupt persisted state.

export type KpiTrustLevel = "trusted" | "experimental" | "unavailable";
export type KpiDirection = "min" | "max" | "target";

export type KpiObjectiveConstraint = {
  kpi_id: string;
  direction: KpiDirection;
  threshold: number;
  baseline_relative: boolean;
};

export type KpiObjectivePredicate = {
  kind: string;
  args: Record<string, unknown>;
};

export type ObjectiveContext = "seven_post" | "replay";
export type ReplayReachability = "reachable" | "conditional" | "unreachable";

export type KpiObjective = {
  id: string;
  display_name: string;
  description: string;
  primary_kpi_id: string | null;
  direction: KpiDirection;
  weights: Record<string, number>;
  constraints: KpiObjectiveConstraint[];
  provenance_predicates: KpiObjectivePredicate[];
  baseline_relative: boolean;
  trust_level: KpiTrustLevel;
  notes: string;
  // 2026-06-11 replay-guardrails build: which Study actuation modes the
  // objective is scoreable on (replay objectives gate on engineering
  // validity, not recorded-truth fidelity), + the probe-grounded
  // reachability tag for replay ("conditional" renders a warning).
  // Optional so payloads predating the field degrade to seven_post.
  contexts?: ObjectiveContext[];
  replay_reachability?: ReplayReachability | null;
};

// The actuation modes an objective is scoreable on; payloads predating
// the field are 7-post objectives by construction.
export function objectiveContexts(o: KpiObjective): ObjectiveContext[] {
  return o.contexts ?? ["seven_post"];
}

export async function fetchObjectives(): Promise<KpiObjective[]> {
  const r = await fetch("/api/kpis/objectives");
  if (!r.ok) {
    throw new Error(`GET /api/kpis/objectives failed: ${r.status}`);
  }
  const j = await r.json();
  if (!Array.isArray(j)) {
    throw new Error(
      `GET /api/kpis/objectives: expected array, got ${typeof j}`,
    );
  }
  return j as KpiObjective[];
}
